/* eslint-disable @typescript-eslint/no-explicit-any */
/** My Tasks — everything assigned to the current user across HEROs projects,
 * grouped by REAL month (multi-year axis derived from actual task dates, not
 * the legacy 2026 month indexes). A task is "mine" when the user is the owner
 * OR appears in the %responsible splits — split members used to be invisible
 * here because only `t.user` (= splits[0]) was matched. */
import { useMemo, useState } from 'react';
import { useData } from '../context/AppDataContext';
import { Ico, CategoryChip, STATUS_META, relTime, RowMenu, useToast } from '../components/ui';
import { taskDates, taskMonthKeys, monthKeyLabel, todayMonthKey, fmtShortDate } from '../components/ui';
import { downloadCsv, dateStamp } from '../lib/exportUtils';

/** Persistable task statuses — the server enum rejects the display-only
 *  'cancelled' entry in STATUS_META, so the selects must not offer it. */
const TASK_STATUS_OPTIONS = ['planned', 'in-progress', 'waiting', 'completed', 'delayed'];

export function MyTasksPage({ currentUser, editTask, updateTimelineStatus, deleteTimeline, openProject }: any) {
  const D = useData();
  const [statusFilter, setStatusFilter] = useState('open');
  const toast = useToast();

  const uid = currentUser.id;
  // Tasks where I hold a compensation ("job") allocation. This was the missing
  // link: a person allocated a job on a task — via the Compensation tab — who
  // was never set as its owner or %responsible saw NOTHING here. A job
  // allocation is real assigned work, so it must surface in My Tasks; clearing
  // the allocation (or my split within it) removes the task again.
  const myCompTaskIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of D.compensations) {
      if (c.taskId && (c.splits ?? []).some((s: any) => s.user === uid)) ids.add(c.taskId);
    }
    return ids;
  }, [D.compensations, uid]);
  // A task is "mine" three ways: I own it, I'm in its %responsible split, OR I
  // hold a job allocation on it.
  const myTasks = useMemo(() => D.timeline.filter((t: any) =>
    (t.rowType ?? 'task') !== 'milestone'
    && (t.user === uid || (t.splits ?? []).some((s: any) => s.user === uid) || myCompTaskIds.has(t.id))
  ), [D.timeline, uid, myCompTaskIds]);

  const todayK = todayMonthKey();
  const todayIso = new Date().toISOString().slice(0, 10);

  // Month buckets spanning the REAL range of my tasks (capped at 6 years),
  // always including the current month so the burden card has a bucket.
  const buckets = useMemo(() => {
    // Spans computed ONCE per task — taskMonthKeys allocates Dates, so calling
    // it inside the months×tasks loop would churn on every timeline change.
    const spans = myTasks.map((t: any) => ({ t, ...taskMonthKeys(t) }));
    let minK = todayK; let maxK = todayK;
    for (const s of spans) { if (s.sK < minK) minK = s.sK; if (s.eK > maxK) maxK = s.eK; }
    if (maxK - minK > 71) maxK = minK + 71;
    const out: Array<{ k: number; label: string; tasks: any[] }> = [];
    for (let k = minK; k <= maxK; k++) {
      out.push({
        k,
        label: monthKeyLabel(k),
        tasks: spans.filter((s) => s.sK <= k && k <= s.eK).map((s) => s.t),
      });
    }
    return out;
  }, [myTasks, todayK]);

  const thisMonthTasks = buckets.find((b) => b.k === todayK)?.tasks ?? [];

  const inProgress = myTasks.filter((t: any) => t.status === 'in-progress').length;
  const waiting = myTasks.filter((t: any) => t.status === 'waiting').length;
  const delayed = myTasks.filter((t: any) => t.status === 'delayed').length;
  const completed = myTasks.filter((t: any) => t.status === 'completed').length;
  const upcoming = myTasks.filter((t: any) => taskMonthKeys(t).sK === todayK && t.status !== 'completed').length;

  const passesFilter = (t: any) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'open') return t.status !== 'completed';
    return t.status === statusFilter;
  };

  const monthBadge = (k: number): { label: string; tone: string } | null => {
    if (k < todayK) return { label: 'Past', tone: 'slate' };
    if (k === todayK) return { label: 'This month', tone: 'red' };
    if (k === todayK + 1) return { label: 'Next month', tone: 'amber' };
    return null;
  };

  const daysUntil = (iso: string) => Math.round((new Date(iso + 'T00:00:00').getTime() - new Date(todayIso + 'T00:00:00').getTime()) / 86400000);

  return (
    <div className="page-pad fadein">
      <div className="page-head">
        <div>
          <h2>My Tasks</h2>
          <div className="sub">Everything assigned to you across HEROs projects · pulled live from each project timeline (owner or % responsible)</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 'auto' }}>
            <option value="open">Open tasks only</option>
            <option value="all">All statuses</option>
            {TASK_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_META[s]?.label ?? s}</option>)}
          </select>
          <button className="btn" onClick={() => {
            const rows = myTasks.filter(passesFilter);
            if (!rows.length) { toast('No tasks to export'); return; }
            downloadCsv(rows, {
              Project: (t: any) => D.projects.find((x) => x.id === t.project)?.code ?? t.project,
              Task: (t: any) => t.name,
              Category: (t: any) => t.category,
              Status: (t: any) => STATUS_META[t.status]?.label ?? t.status,
              Start: (t: any) => taskDates(t).startDate,
              End: (t: any) => taskDates(t).endDate,
            }, `my-tasks-${dateStamp()}.csv`);
            toast(`Exported ${rows.length} tasks`);
          }}><Ico.dl /> Export</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 14 }}>
        <div className="stat" style={{ background: 'var(--navy)', color: '#fff', borderColor: 'var(--navy)' }}>
          <div className="label" style={{ color: 'rgba(255,255,255,0.7)' }}>Total assigned</div>
          <div className="v tabular" style={{ fontSize: 26 }}>{myTasks.length}</div>
          <div className="d" style={{ color: 'rgba(255,255,255,0.7)' }}>across {new Set(myTasks.map((t: any) => t.project)).size} projects</div>
        </div>
        <div className="stat"><div className="label">In progress</div><div className="v tabular" style={{ color: 'var(--t-blue-fg)' }}>{inProgress}</div><div className="d">currently active</div></div>
        <div className="stat"><div className="label">Starting this month</div><div className="v tabular" style={{ color: 'var(--accent)' }}>{upcoming}</div><div className="d">begin in {monthKeyLabel(todayK)}</div></div>
        <div className="stat"><div className="label">Waiting / delayed</div><div className="v tabular" style={{ color: 'var(--t-amber-fg)' }}>{waiting + delayed}</div><div className="d">{waiting} waiting · {delayed} delayed</div></div>
        <div className="stat"><div className="label">Completed</div><div className="v tabular" style={{ color: 'var(--t-green-fg)' }}>{completed}</div><div className="d">finished so far</div></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14, alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Honest empty state: you DO have tasks, the current filter just hides
              them all (e.g. every task is completed and the filter is "Open"). */}
          {myTasks.length > 0 && !buckets.some((b) => b.tasks.filter(passesFilter).length > 0) && (
            <div className="card" style={{ padding: '14px 16px', fontSize: 12.5, color: 'var(--text-2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <span>You have {myTasks.length} task{myTasks.length === 1 ? '' : 's'}, but none match “{statusFilter === 'open' ? 'Open tasks only' : STATUS_META[statusFilter]?.label ?? statusFilter}”. {completed > 0 && `${completed} completed.`}</span>
              {statusFilter !== 'all' && <button className="btn sm" onClick={() => setStatusFilter('all')}>Show all statuses</button>}
            </div>
          )}
          {buckets.map((b) => {
            const visible = b.tasks.filter(passesFilter);
            const isPast = b.k < todayK;
            // Keep the list scannable: skip empty months except the current one.
            if (visible.length === 0 && b.k !== todayK) return null;
            const badge = monthBadge(b.k);
            return (
              <div key={b.k} className="card" style={{ opacity: isPast ? 0.6 : 1 }}>
                <div className="card-h" style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h3 style={{ fontSize: 13 }}>{b.label}</h3>
                    {badge && <span className="chip" data-tone={badge.tone}><span className="dot" />{badge.label}</span>}
                    <span className="sub" style={{ marginLeft: 4 }}>{visible.length} task{visible.length === 1 ? '' : 's'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, fontSize: 11, color: 'var(--text-2)' }}>
                    {visible.filter((t: any) => t.status === 'in-progress').length > 0 && <span><span className="status-dot in-progress" />{visible.filter((t: any) => t.status === 'in-progress').length} in progress</span>}
                    {visible.filter((t: any) => t.status === 'planned').length > 0 && <span><span className="status-dot planned" />{visible.filter((t: any) => t.status === 'planned').length} planned</span>}
                  </div>
                </div>
                {visible.length === 0 ? (
                  <div style={{ padding: '14px 16px', color: 'var(--text-3)', fontSize: 12 }}>No tasks in {b.label}.</div>
                ) : (
                  <div>
                    {visible.map((t: any) => {
                      const proj = D.projects.find((x) => x.id === t.project);
                      const tone = (D.categories[t.category] || { tone: 'slate' }).tone;
                      const td = taskDates(t);
                      const { sK, eK } = taskMonthKeys(t);
                      const isStarting = sK === b.k;
                      const isEnding = eK === b.k;
                      const totalMonths = eK - sK + 1;
                      const monthOf = b.k - sK + 1;
                      const isDeadline = isEnding && (t.status === 'in-progress' || t.status === 'planned' || t.status === 'waiting');
                      return (
                        <div key={t.id + ':' + b.k} className="hover-row" style={{ display: 'grid', gridTemplateColumns: '24px 90px 1fr 130px 150px 130px 26px', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => editTask(t)}>
                          <input type="checkbox" checked={t.status === 'completed'} onClick={(e) => e.stopPropagation()} onChange={() => { updateTimelineStatus(t.id, t.status === 'completed' ? 'in-progress' : 'completed'); toast(t.status === 'completed' ? 'Marked in progress' : 'Marked completed'); }} />
                          <span className="mono" style={{ fontWeight: 600, fontSize: 12, color: 'var(--accent)', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); openProject(t.project, 'timeline'); }}>{proj?.code ?? '—'}</span>
                          <div>
                            <div style={{ fontWeight: 500, fontSize: 12.5, textDecoration: t.status === 'completed' ? 'line-through' : 'none', color: t.status === 'completed' ? 'var(--text-3)' : 'var(--text)' }}>{t.name}</div>
                            <div style={{ fontSize: 10.5, color: 'var(--text-2)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <CategoryChip category={t.category} />
                              <span style={{ color: 'var(--text-3)' }} className="mono">{proj?.shortTitle ?? ''}</span>
                              {totalMonths > 1 && <span style={{ color: 'var(--text-3)' }}>· month {monthOf} of {totalMonths}</span>}
                            </div>
                          </div>
                          <select
                            value={t.status}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { updateTimelineStatus(t.id, e.target.value as any); toast('Status updated'); }}
                            style={{ width: '100%', fontSize: 11.5, padding: '3px 4px' }}>
                            {TASK_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_META[s]?.label ?? s}</option>)}
                          </select>
                          <div style={{ fontSize: 11.5, color: 'var(--text-2)' }}>
                            {isDeadline ? (
                              <span style={{ color: 'var(--accent)', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <Ico.flag /> Due {fmtShortDate(td.endDate)}
                              </span>
                            ) : isStarting ? (
                              <span>Starts {fmtShortDate(td.startDate)}</span>
                            ) : (
                              <span>through {fmtShortDate(td.endDate)}</span>
                            )}
                            {t.lastUpdate && (
                              <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>
                                Last updated {relTime(t.lastUpdate.at)}
                              </div>
                            )}
                          </div>
                          <div className="bar-track" style={{ height: 4 }}>
                            <div style={{ width: (monthOf / totalMonths * 100) + '%', height: '100%', background: `var(--t-${tone}-fg)` }} />
                          </div>
                          <RowMenu items={[
                            { label: 'Edit task', onClick: () => editTask(t) },
                            { label: 'Open project timeline', onClick: () => openProject(t.project, 'timeline') },
                            { label: t.status === 'completed' ? 'Mark in progress' : 'Mark completed', onClick: () => { updateTimelineStatus(t.id, t.status === 'completed' ? 'in-progress' : 'completed'); toast(t.status === 'completed' ? 'Marked in progress' : 'Marked completed'); } },
                            { label: 'Delete task', danger: true, disabled: !deleteTimeline, onClick: () => { if (deleteTimeline && window.confirm(`Delete task "${t.name}"? This cannot be undone.`)) { deleteTimeline(t.id); toast('Task deleted'); } } },
                          ]} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, position: 'sticky', top: 8 }}>
          <div className="card">
            <div className="card-h"><h3>Upcoming deadlines</h3></div>
            <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {myTasks
                .filter((t: any) => t.status !== 'completed' && taskDates(t).endDate >= todayIso)
                .sort((a: any, b: any) => (taskDates(a).endDate < taskDates(b).endDate ? -1 : 1))
                .slice(0, 8)
                .map((t: any) => {
                  const proj = D.projects.find((x) => x.id === t.project);
                  const tone = (D.categories[t.category] || { tone: 'slate' }).tone;
                  const td = taskDates(t);
                  const days = daysUntil(td.endDate);
                  return (
                    <div key={t.id} style={{ padding: 8, borderLeft: `3px solid var(--t-${tone}-fg)`, background: 'var(--surface-alt)', borderRadius: 4, cursor: 'pointer' }} onClick={() => editTask(t)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                        <span className="mono" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)' }}>{proj?.code ?? '—'}</span>
                        <span style={{ fontSize: 10.5, color: days <= 14 ? 'var(--accent)' : 'var(--text-2)', fontWeight: 500 }}>
                          {days === 0 ? 'due today' : days === 1 ? 'due tomorrow' : days < 31 ? `in ${days} d` : `in ${Math.round(days / 30.44)} mo`}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, marginTop: 2, fontWeight: 500 }}>{t.name}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-2)', marginTop: 2 }}>Due {fmtShortDate(td.endDate)} {td.endDate.slice(0, 4)}</div>
                    </div>
                  );
                })}
              {myTasks.filter((t: any) => t.status !== 'completed' && taskDates(t).endDate >= todayIso).length === 0 && (
                <div style={{ color: 'var(--text-3)', fontSize: 12, textAlign: 'center', padding: 10 }}>No open deadlines ahead.</div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-h"><h3>By project</h3></div>
            <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[...new Set(myTasks.map((t: any) => t.project))].map((pid: any) => {
                const proj = D.projects.find((x) => x.id === pid);
                const tasks = myTasks.filter((t: any) => t.project === pid);
                const done = tasks.filter((t: any) => t.status === 'completed').length;
                return (
                  <div key={pid} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 6, borderRadius: 4, cursor: 'pointer' }} onClick={() => openProject(pid, 'timeline')} className="hover-row">
                    <span className="mono" style={{ fontWeight: 600, fontSize: 11, color: 'var(--accent)', width: 70 }}>{proj?.code ?? '—'}</span>
                    <span style={{ flex: 1, fontSize: 12 }}>{proj?.shortTitle ?? 'Unknown project'}</span>
                    <span className="mono tabular" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{done}/{tasks.length}</span>
                  </div>
                );
              })}
              {myTasks.length === 0 && <div style={{ color: 'var(--text-3)', fontSize: 12, textAlign: 'center', padding: 10 }}>Nothing assigned to you yet.</div>}
            </div>
          </div>

          <div className="card">
            <div className="card-h"><h3>Burden this month</h3></div>
            <div className="card-b">
              {(() => {
                const count = thisMonthTasks.filter((t: any) => t.status !== 'completed').length;
                const lvl = count === 0 ? 'Free' : count <= 2 ? 'Light' : count <= 4 ? 'Moderate' : 'Heavy';
                const tone = count === 0 ? 'slate' : count <= 2 ? 'green' : count <= 4 ? 'amber' : 'red';
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div className="v tabular" style={{ fontSize: 30, fontWeight: 700, color: `var(--t-${tone === 'red' ? 'amber' : tone}-fg)` }}>{count}</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}><span className="chip" data-tone={tone}><span className="dot" />{lvl}</span></div>
                      <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 4 }}>open tasks in {monthKeyLabel(todayK)}</div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
